import type { SurfaceRole } from "@/components/ui/surface-contract";
import type { CubicBezier } from "@/lib/reveal-motion";

/** A plate frame in viewport CSS px. */
export type NativeGlassFrame = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/**
 * Native animates the plate on the shared wall clock instead of snapping it
 * to each sampled rect, so IPC delay changes only when the plate starts.
 */
export type NativeGlassMorph = {
  /** A new id starts a new morph. */
  id: string;
  /** Omitted: native starts from the plate's current frame. */
  from?: NativeGlassFrame & { cornerRadius: number };
  /** Omitted: native ends on the live sampled rect. */
  to?: NativeGlassFrame;
  /** Epoch ms the plate starts moving (`Date.now() + PLATE_LEAD_MS`). */
  startedAt: number;
  /** Native chooses and acknowledges the clock after installing the plate. */
  startOnApply?: boolean;
  durationMs: number;
  bezier: CubicBezier;
};

export type NativeGlassRect = NativeGlassFrame & {
  /**
   * Omitted for surfaces whose radius is owned by the native role table
   * (Rust falls back to `role.corner_radius` and clamps to half the box).
   * The input bar omits it: rail-role 24px + the native clamp yields the
   * single-line capsule and fixed corners on a grown bar.
   */
  cornerRadius?: number;
  /** Apple's continuous corner, where the webview cuts the surface to it
   *  (`--native-corner-curve`); omitted: circular, as `border-radius`. */
  cornerCurve?: "continuous";
  role: SurfaceRole;
  group?: string;
  forcedMode?: "visual-effect";
  /** Debug override from the glass audit: NSGlassEffectView private variant. */
  variant?: number;
  /** The plate's opacity (0–1); native fades to it, hiding the plate at 0. */
  opacity?: number;
  morph?: NativeGlassMorph;
};

/** Rounds to the 0.5px grid of every plate frame the webview sends native. */
export const roundToHalfPixel = (value: number) => Math.round(value * 2) / 2;

const parseCssRadius = (value: string) => {
  const match = value.match(/^[\d.]+/);
  return match ? Number(match[0]) : 0;
};

export const readRenderedCornerRadius = (
  element: HTMLElement,
  rect: DOMRect,
  resolveStyle: (target: HTMLElement) => CSSStyleDeclaration,
) => {
  const style = resolveStyle(element);
  const cssRadius = parseCssRadius(
    style.borderTopLeftRadius ||
      style.borderRadius ||
      element.style.borderTopLeftRadius ||
      element.style.borderRadius,
  );
  if (cssRadius <= 0) return 0;

  const widthScale =
    element.offsetWidth > 0 ? rect.width / element.offsetWidth : 1;
  const heightScale =
    element.offsetHeight > 0 ? rect.height / element.offsetHeight : 1;
  const scale = Math.max(0, Math.min(widthScale, heightScale));
  // Browsers clamp border-radius to half the box, so pill surfaces (999px)
  // must report their rendered capsule radius, not the raw CSS value.
  const clamped = Math.min(
    cssRadius * scale,
    Math.min(rect.width, rect.height) / 2,
  );
  return roundToHalfPixel(clamped);
};

/** The corner curve a surface's CSS asks its native plate for. */
export const readNativeCornerCurve = (
  style: CSSStyleDeclaration,
): "continuous" | undefined =>
  style.getPropertyValue("--native-corner-curve").trim() === "continuous"
    ? "continuous"
    : undefined;

export type BatchDelta = "none" | "geometry" | "full";

const nativeRectIdentity = (rect: NativeGlassRect) =>
  JSON.stringify([
    rect.role ?? "",
    rect.group ?? "",
    rect.forcedMode ?? "",
    rect.variant ?? null,
  ]);

const sameNativeRectIdentities = (
  prev: NativeGlassRect[],
  next: NativeGlassRect[],
) => {
  const counts = new Map<string, number>();
  for (const rect of prev) {
    const key = nativeRectIdentity(rect);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  for (const rect of next) {
    const key = nativeRectIdentity(rect);
    const count = counts.get(key);
    if (!count) return false;
    if (count === 1) counts.delete(key);
    else counts.set(key, count - 1);
  }
  return counts.size === 0;
};

/**
 * Classify a batch against the last committed one. "geometry" means the same
 * native surfaces/materials with only numbers, morphs or order changed — the
 * gesture hot path — letting Rust skip material setters while still
 * reconciling frame positions and any order that actually matters. A rect on
 * the same explicit morph (`to` set) keeps its geometry: native ignores its
 * samples until the morph ends, and a new id or the end still sends.
 */
export const classifyRectsDelta = (
  prev: NativeGlassRect[],
  next: NativeGlassRect[],
): BatchDelta => {
  if (prev.length !== next.length) return "full";
  let geometryChanged = false;
  for (let index = 0; index < next.length; index += 1) {
    const a = prev[index];
    const b = next[index];
    if (!a || !b) return "full";
    if (
      a.role !== b.role ||
      a.group !== b.group ||
      a.forcedMode !== b.forcedMode ||
      a.variant !== b.variant
    ) {
      return sameNativeRectIdentities(prev, next) ? "geometry" : "full";
    }
    if (a.morph?.id !== b.morph?.id || a.opacity !== b.opacity) {
      geometryChanged = true;
    } else if (
      !a.morph?.to &&
      (a.x !== b.x ||
        a.y !== b.y ||
        a.width !== b.width ||
        a.height !== b.height ||
        a.cornerRadius !== b.cornerRadius ||
        a.cornerCurve !== b.cornerCurve)
    ) {
      geometryChanged = true;
    }
  }
  return geometryChanged ? "geometry" : "none";
};
